import type { DeskDetail, DeskGoal } from '@deskworks/shared-types';
import { Pencil, Plus, Target, Trash2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

export function DeskGoalsSection({ desk }: { desk: DeskDetail }) {
  const remove = useApiMutation((goalId: string) => api.desks.removeGoal(desk.id, goalId), {
    success: t('Goal removed'),
    invalidate: [qk.desk(desk.id)],
  });

  return (
    <Section
      icon={<Target />}
      title={t('Goals')}
      description={t('What this desk should achieve. The Playbook says how.')}
      actions={
        <GoalDialog desk={desk}>
          <Button variant="outline" size="sm">
            <Plus /> {t('Add')}
          </Button>
        </GoalDialog>
      }
    >
      {desk.goals.length === 0 ? (
        <EmptyState compact title={t('No goals yet')} description={t('e.g. “Every case is resolved within 48 hours.”')} />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {desk.goals.map((g) => (
            <li key={g.id} className="flex items-start gap-1 rounded-md border px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{g.title}</div>
                {g.description && <div className="line-clamp-3 text-xs text-muted-foreground">{g.description}</div>}
              </div>
              <GoalDialog desk={desk} goal={g}>
                <Button variant="ghost" size="icon-sm" className="size-6" aria-label={t('Edit goal')}>
                  <Pencil />
                </Button>
              </GoalDialog>
              <Button
                variant="ghost"
                size="icon-sm"
                className="-mr-1 size-6"
                aria-label={t('Remove goal')}
                onClick={() => remove.mutate(g.id)}
                disabled={remove.isPending}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function GoalDialog({ desk, goal, children }: { desk: DeskDetail; goal?: DeskGoal; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const save = useApiMutation(
    () =>
      goal
        ? api.desks.updateGoal(desk.id, goal.id, { title: title.trim(), description: description.trim() })
        : api.desks.addGoal(desk.id, { title: title.trim(), description: description.trim() || undefined }),
    {
      success: goal ? t('Goal saved') : t('Goal added'),
      invalidate: [qk.desk(desk.id)],
      onSuccess: () => setOpen(false),
    },
  );

  const onOpenChange = (o: boolean) => {
    if (o) {
      setTitle(goal?.title ?? '');
      setDescription(goal?.description ?? '');
    }
    setOpen(o);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim()) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{goal ? t('Edit goal') : t('New goal')}</DialogTitle>
            <DialogDescription>{t('A goal is an outcome the desk works towards, not a single task. Every Agent on the desk sees it.')}</DialogDescription>
          </DialogHeader>
          <Field label={t('Goal')}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('e.g. Every case is resolved within 48 hours')} required autoFocus />
          </Field>
          <Field label={t('Details')} optional>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder={t('Why it matters, how it’s measured, exceptions …')} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!title.trim()}>
              {goal ? t('Save') : t('Add goal')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
